import { queryOptions, useQuery } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

import { FeatureFlagResolved } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { useUser } from '@cast/infrastructure-auth';
import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { overrideOrganizationHeader } from '@cast/api-client';

const FLAGS_QUERY_KEY = 'flags';

const getFlagsQueryKey = (params: {
  organizationId?: string;
  clusterId?: string;
  flagNamePrefix?: string;
  flagName?: string;
  targetingKey?: string;
  userId?: string;
}) =>
  [
    FLAGS_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.flagNamePrefix,
    params.flagName,
    params.targetingKey,
    params.userId,
  ] as const;

type FlagsQueryParams = {
  organizationId?: string;
  userId?: string;
  clusterId?: string;
  flagNamePrefix?: string;
  flagName?: string;
  targetingKey?: string;
};

const getFlagsQueryOptions = ({
  organizationId,
  userId,
  clusterId,
  flagNamePrefix,
  flagName,
  targetingKey,
  enabled = true,
  currentOrganization,
  user,
}: FlagsQueryParams & {
  enabled?: boolean;
  currentOrganization?: any;
  user?: any;
}) =>
  queryOptions<FeatureFlagResolved[]>({
    queryKey: getFlagsQueryKey({
      organizationId,
      clusterId,
      flagNamePrefix,
      flagName,
      targetingKey,
      userId,
    }),
    retry: 3,
    queryFn: async () => {
      const { data } = await apiClient.featureFlags.resolveFlags(
        {
          message: {
            flagNamePrefix,
            flagName,
            targetingKey,
            payload: {
              context: {
                // TODO (Justas): Libs splitting - add .d.ts files
                environment:
                  (import.meta as any).env.FF_ENV ??
                  (window as any).env.VITE_ENV,
                organizationId,
                userId,
                clusterId,
              },
            },
          },
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );

      return data.items ?? [];
    },
    enabled: makeIsEnabled(
      !isEmpty(currentOrganization),
      !isEmpty(user),
      enabled
    ),
  });

type UseFlagsQueryArgs = {
  clusterId?: string;
  flagNamePrefix?: string;
  flagName?: string;
  enabled?: boolean;
  targetingKey?: string;
  organizationId?: string;
};

export const useFlagsQuery = ({
  clusterId,
  flagName,
  flagNamePrefix,
  targetingKey,
  enabled = true,
  organizationId: organizationIdOverride,
}: UseFlagsQueryArgs = {}) => {
  const { currentOrganization, enterpriseOrgId } = useOrganizations();
  const user = useUser();
  // Prefer enterprise org ID (URL or child parentId) so enterprise-assigned
  // UI flags cascade to children outside /organization/enterprise/:id/* routes.
  const organizationId =
    organizationIdOverride ||
    enterpriseOrgId ||
    currentOrganization?.parentId ||
    currentOrganization?.id;

  return useQuery(
    getFlagsQueryOptions({
      organizationId,
      userId: user?.id,
      clusterId,
      flagNamePrefix,
      flagName,
      targetingKey,
      enabled,
      currentOrganization,
      user,
    })
  );
};
